import type { RefObject } from 'react'
import type React from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { isIosPlatform } from './browser'
import { getActualWidthOfChars, getStyle, getStyleToNumber } from './helper'
import type { UseUncontrolledInput } from './types'
import { Enum } from './types'

export function useMounted(f: () => any): void {
    const mounted = useRef(false)

    useEffect(() => {
        if (mounted.current) {
            return
        }
        mounted.current = true
        return f()
    }, [f])
}

export function useLayoutMounted(f: () => any): void {
    const mounted = useRef(false)

    useLayoutEffect(() => {
        if (mounted.current) {
            return
        }
        mounted.current = true
        return f()
    }, [f])
}

/**
 * 函数防抖让 value 延时改变提高性能
 * @export
 * @param {*} value
 * @param {number} delay
 * @return {*}
 */
export function useDebounce<T>(value: T, delay: number): T {
    const [debounceValue, setDebounceValue] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebounceValue(value)
        }, delay)
        return () => {
            clearTimeout(timer)
        }
    }, [value, delay])

    return debounceValue
}

export interface UseDelayedRenderOptions {
    enterDelay?: number
    exitDelay?: number
    onUnmount?: () => void
}

export const useDelayedRender = (active = false, options: UseDelayedRenderOptions = {}) => {
    const [, force] = useState<unknown>()
    const mounted = useRef(active)
    const rendered = useRef(false)
    const renderTimer = useRef<number | null>(null)
    const unmountTimer = useRef<number | null>(null)
    const prevActive = useRef(active)

    const recalculate = useCallback(() => {
        const { enterDelay = 1, exitDelay = 0 } = options

        if (prevActive.current) {
            // Mount immediately
            mounted.current = true
            if (unmountTimer.current) {
                clearTimeout(unmountTimer.current)
            }

            if (enterDelay <= 0) {
                // Render immediately
                rendered.current = true
            } else {
                if (renderTimer.current) {
                    return
                }
                // Render after a delay
                renderTimer.current = window.setTimeout(() => {
                    rendered.current = true
                    renderTimer.current = null
                    force({})
                }, enterDelay)
            }
        } else {
            // Immediately set to unrendered
            rendered.current = false

            if (exitDelay <= 0) {
                mounted.current = false
            } else {
                if (unmountTimer.current) {
                    return
                }

                // Unmount after a delay
                unmountTimer.current = window.setTimeout(() => {
                    mounted.current = false
                    unmountTimer.current = null
                    force({})
                }, exitDelay)
            }
        }
    }, [options])

    // When the active prop changes, need to re-calculate
    if (active !== prevActive.current) {
        prevActive.current = active
        // We want to do this synchronously with the render, not in an effect
        // this way when active → true, mounted → true in the same pass
        recalculate()
    }

    return {
        mounted: mounted.current,
        rendered: rendered.current
    }
}

export const BreakPointSize = Enum('xs', 'sm', 'md', 'lg')
export type BreakPointSize = Enum<typeof BreakPointSize>

export function getBreakpointSize(width: number) {
    let size: BreakPointSize = BreakPointSize.md
    if (width >= 720) {
        size = BreakPointSize.lg
    } else if (width >= 598) {
        size = BreakPointSize.md
    } else if (width >= 440) {
        size = BreakPointSize.sm
    } else {
        size = BreakPointSize.xs
    }
    return size
}

const EMPTY_DEPS: React.DependencyList = []

/**
 * 获取响应式断点信息（改进版本，解决尺寸跳动问题）
 */
export const useBreakpoint = <T extends HTMLElement = HTMLDivElement>(
    deps: React.DependencyList = EMPTY_DEPS,
    options: {
        scale?: number
        debounceMs?: number
    } = {}
) => {
    const { debounceMs = 50, scale = 1 } = options
    const ref = useRef<T | null>(null)
    const timeoutRef = useRef<number>()
    // const [height, setHeight] = useState<number>(0)
    // const [width, setWidth] = useState<number>(0)
    const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
    const [breakPoint, setBreakPoint] = useState<BreakPointSize>()
    const { width, height } = size
    const updateSize = useCallback((dimensions: { width: number; height: number }) => {
        const breakPointSize = getBreakpointSize(dimensions.width)
        setSize(dimensions)
        setBreakPoint(breakPointSize)
    }, [])

    const debouncedUpdateSize = useCallback(
        (dimensions: { width: number; height: number }) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            timeoutRef.current = window.setTimeout(() => {
                updateSize(dimensions)
            }, debounceMs)
        },
        [debounceMs, updateSize]
    )

    useEffect(() => {
        const container = ref.current
        if (!container) {
            return
        }

        // 获取内容区域尺寸（与ResizeObserver的contentRect完全一致）
        const getContentSize = (element: Element) => {
            const styles = window.getComputedStyle(element)
            const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0
            const paddingRight = Number.parseFloat(styles.paddingRight) || 0
            const borderLeft = Number.parseFloat(styles.borderLeftWidth) || 0
            const borderRight = Number.parseFloat(styles.borderRightWidth) || 0
            const paddingTop = Number.parseFloat(styles.paddingTop) || 0
            const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0
            const borderTop = Number.parseFloat(styles.borderTopWidth) || 0
            const borderBottom = Number.parseFloat(styles.borderBottomWidth) || 0

            const { width, height } = element.getBoundingClientRect()
            return {
                width: width - paddingLeft - paddingRight - borderLeft - borderRight,
                height: height - paddingTop - paddingBottom - borderTop - borderBottom
            }
        }

        // 初始化尺寸（与ResizeObserver保持完全一致）
        const initialSize = getContentSize(container)
        const { width, height } = initialSize
        updateSize({ width: Math.round((width / scale) * 100) / 100, height: Math.round((height / scale) * 100) / 100 })

        const observer = new ResizeObserver(entries => {
            const { contentRect } = entries[entries.length - 1]
            debouncedUpdateSize(contentRect)
        })

        observer.observe(container)

        return () => {
            observer.unobserve(container)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, scale])

    return { ref, breakPoint, width, height }
}

/**
 *
 * @param param0
 * @returns
 *  在使用的组件内：
    const [_value, handleChange] = useUncontrolled({
        value: contextProps.checked ?? checked,
        defaultValue: defaultChecked,
        finalValue: false,
    });

    使用的地方用：_value
    组件改值的地方：
        ctx ? contextProps.onChange(event) : onChange?.(event);
        handleChange(event.currentTarget.checked);
 */
export function useUncontrolled<T>({
    value,
    defaultValue,
    finalValue,

    onChange = () => {}
}: UseUncontrolledInput<T>): [T, (value: T) => void, boolean] {
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue === undefined ? finalValue : defaultValue)

    const handleUncontrolledChange = (val: T) => {
        setUncontrolledValue(val)
        onChange?.(val)
    }

    if (value !== undefined) {
        return [value, onChange, true]
    }

    return [uncontrolledValue as T, handleUncontrolledChange, false]
}

/**
 * 获取当前文本dom文本是否被省略
 * 使用时建议直接绑定文本标签
 */

export const useTextOmit = function <T extends HTMLElement = any>(dependencies?: string[]) {
    const ref = useRef<T>(null)
    const [isOmit, setIsOmit] = useState(false)
    const [rangeWidth, setRangeWidth] = useState(0)
    const [targetWidth, setTargetWidth] = useState(0)
    const frameID = useRef(0)
    const currentDependencies = useMemo(() => dependencies, [dependencies])

    const changeIsOmit = useCallback((target: HTMLElement) => {
        const options = { size: getStyleToNumber(target, 'font-size'), family: getStyle(target, 'font-family') ?? 'Microsoft YaHei' }
        const rangeWidth = getActualWidthOfChars(target.textContent ?? '', options)
        setRangeWidth(rangeWidth)
        const otherMargin =
            getStyleToNumber(target, 'padding-right') +
            getStyleToNumber(target, 'padding-left') +
            getStyleToNumber(target, 'border-left-width') +
            getStyleToNumber(target, 'border-right-width')
        const { width } = target.getBoundingClientRect()
        const targetWidth = width - otherMargin
        setTargetWidth(targetWidth)
        if (rangeWidth > targetWidth) {
            setIsOmit(true)
            return
        }
        setIsOmit(false)
    }, [])

    const observerCallback = useCallback(
        (entries: MutationRecord[]) => {
            const entry = entries[0]
            if (entry) {
                cancelAnimationFrame(frameID.current)
                frameID.current = requestAnimationFrame(() => {
                    if (ref.current) {
                        const { target } = entry
                        if (target instanceof HTMLElement) {
                            changeIsOmit(target)
                            return
                        }
                        changeIsOmit(target.parentNode as HTMLElement)
                    }
                })
            }
        },
        [changeIsOmit]
    )

    useEffect(() => {
        if (!ref.current) {
            return
        }
        const target = ref.current
        requestAnimationFrame(() => {
            changeIsOmit(target)
        })
    }, [currentDependencies, changeIsOmit])

    useEffect(() => {
        const observer = new MutationObserver(observerCallback)
        if (ref.current) {
            observer?.observe(ref.current, {
                childList: true,
                characterData: true,
                attributes: true,
                subtree: true
            })
        }
        return () => {
            observer?.disconnect()
            if (frameID.current) {
                cancelAnimationFrame(frameID.current)
            }
        }
    }, [observerCallback])

    return { ref, isOmit, targetWidth, rangeWidth }
}

export const useDomHeight: () => [RefObject<HTMLTextAreaElement>, number | undefined] = () => {
    const domRef = useRef<HTMLTextAreaElement>(null)

    // const dom = document.querySelector<HTMLDivElement>(domId)
    const [height, setHeight] = useState<number | undefined>(domRef.current?.scrollHeight)

    const obsCallback = useCallback((mutations: MutationRecord[], observer: MutationObserver) => {
        if (mutations.length > 0 && domRef.current) {
            if (!domRef.current.scrollHeight) {
                setHeight(domRef.current.clientHeight)
                return
            }
            setHeight(domRef.current.scrollHeight)
            // heightRef.current = mutations[0].target.scrollHeight || undefined
        }
    }, [])

    useEffect(() => {
        if (!domRef.current) {
            return
        }
        const target = domRef.current
        if (!target.scrollHeight) {
            setHeight(target.clientHeight)
            return
        }
        setHeight(target.scrollHeight)
    }, [])

    useEffect(() => {
        if (!domRef.current) {
            return
        }
        const mutationObserver = new MutationObserver(obsCallback)
        mutationObserver.observe(domRef.current, {
            childList: true,
            attributes: true,
            attributeFilter: ['style'],
            attributeOldValue: true
            // characterData: true
        })

        return () => {
            mutationObserver.disconnect()
        }
    }, [obsCallback])

    return [domRef, height]
}

// 修复ios下关闭键盘后，实际的元素定位错乱问题
export const useIosOpenKeyboardFix = () => {
    const scrollTopRef = useRef(0)

    const elFocusHandle = useCallback((e: FocusEvent) => {
        if (e.target instanceof HTMLElement && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
            scrollTopRef.current = document.documentElement.scrollTop
        }
    }, [])

    const elBlurHandle = useCallback((e: FocusEvent) => {
        if (e.target instanceof HTMLElement && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
            setTimeout(() => {
                window.scrollTo(0, scrollTopRef.current)
            }, 0)
        }
    }, [])

    useEffect(() => {
        if (isIosPlatform()) {
            document.addEventListener('focus', elFocusHandle, true)
            window.addEventListener('blur', elBlurHandle, true)
        }

        return () => {
            document.removeEventListener('focus', elFocusHandle, true)
            window.removeEventListener('blur', elBlurHandle, true)
        }
    }, [elBlurHandle, elFocusHandle])
}
